import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../../contexts/SocketContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/Toast';
import AdminNotificationBell from '../../components/AdminNotificationBell';
import TabBar from '../../components/TabBar';
import api from '../../utils/api';
import { ClipboardList, Puzzle, Layers } from 'lucide-react';
import { useBizMode, setBizMode } from '../../utils/bizMode';
import { ArrowLeft, RefreshCw, TrendingUp, Loader, ChevronDown, ChevronRight, Check } from 'lucide-react';

// ══════════════════════════════════════════════════════════════════════════════
// 交单报表 · 排五 / 证券 共用版式
//   两个业务的页面结构完全一样：头部 → 三项标签 → 日期 + 代理选择器 → 已选提示 → 汇总卡 → 按代理分组的成员表。
//   差异只在“列”上（排五：提量/成功/图片/重复/无效/失败；证券：领取/成功/待核/作废），
//   所以版式只写一份（AgentReport），排五、证券各给一份 view 配置，说明有哪些列、怎么汇总。
//   以后改报表的样式，只改这一处，两个业务自然保持一致。
// ══════════════════════════════════════════════════════════════════════════════

const AGENT_COLORS = ['#007AFF','#34C759','#AF52DE','#FF9500','#FF2D55','#5AC8FA'];

// ── 转换率工具 ──
function rateInfo(dispatch, qualified) {
  if (!dispatch) return { cl: 'd', text: '—', color: 'var(--c-text-tertiary)' };
  const v = qualified / dispatch;
  if (v >= 0.15) return { cl: 'g', text: (v * 100).toFixed(1) + '%', color: 'var(--c-success)' };
  if (v >= 0.08) return { cl: 'o', text: (v * 100).toFixed(1) + '%', color: 'var(--c-accent)' };
  return { cl: 'r', text: (v * 100).toFixed(1) + '%', color: 'var(--c-danger)' };
}

// ── 转换率药丸（缩小以适配列宽52px）──
function RatePill({ dispatch, qualified, size = 'md' }) {
  const ri = rateInfo(dispatch, qualified);
  const bgMap = { g: 'rgba(52,199,89,0.10)', o: 'rgba(255,149,0,0.10)', r: 'rgba(255,59,48,0.09)', d: 'transparent' };
  const h = size === 'sm' ? 18 : 22;
  const fs = size === 'sm' ? 10 : 11;
  const minW = size === 'sm' ? 38 : 46;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      minWidth: minW, height: h, padding: '0 4px',
      borderRadius: 5,
      background: bgMap[ri.cl], color: ri.color,
      fontSize: fs, fontWeight: ri.cl === 'd' ? 400 : 700,
      fontVariantNumeric: 'tabular-nums',
    }}>{ri.text}</span>
  );
}

// 某个代理名下所有人的 id（沿归属链往下）；includeRoot = 是否把代理自己也算进去
function collectIds(childrenOf, rootId, includeRoot) {
  const ids = new Set(includeRoot ? [rootId] : []);
  const seen = new Set([rootId]);
  const q = [rootId];
  while (q.length) {
    const cur = q.shift();
    for (const child of (childrenOf.get(cur) || [])) {
      if (!seen.has(child)) { seen.add(child); ids.add(child); q.push(child); }
    }
  }
  return ids;
}

const thStyle = (w) => ({
  width: w, textAlign: 'right', fontSize: 10, fontWeight: 600,
  color: 'var(--c-text-secondary)', flexShrink: 0,
});

// ── 排五的列 ──────────────────────────────────────────────────────────────────
// 7列数字区合计 = 32+32+32+28+28+28+52 = 232px；成员名字列 flex:1 自动占剩余
const COL = { d: 32, s: 32, u: 32, dup: 28, inv: 28, fail: 28, r: 52 };

const LEGACY_VIEW = {
  key: 'legacy',
  emptyAll: '今日暂无成员上传记录',
  emptyAgent: '该代理商名下今日暂无数据',
  // 这一天有没有数据（没有的人不进列表）
  active: (m) => m.dispatchCount > 0 || m.uploadCount > 0,
  // 代理选择器里“N 人”和转换率药丸按谁算（排五：提过量的人）
  counted: (m) => m.dispatchCount > 0,
  // 列表排序：成功多的在前
  score: (m) => m.ocrQualified || 0,
  // 汇总（分组头、汇总卡、选择器共用）：base = 转换率分母，ok = 分子
  sum: (list) => {
    const D = list.reduce((s, m) => s + (m.dispatchCount || 0), 0);
    const S = list.reduce((s, m) => s + (m.ocrQualified || 0), 0);
    const U = list.reduce((s, m) => s + m.uploadCount, 0);
    return { D, S, U, base: D, ok: S };
  },
  summary: (t) => [
    { val: t.U, label: '上传张数', color: 'var(--c-primary)' },
    { val: t.S, label: '成功单数', color: 'var(--c-success)' },
    { val: t.D, label: '提取条数', color: 'var(--c-text)' },
    { val: rateInfo(t.base, t.ok).text, label: '转换率', color: rateInfo(t.base, t.ok).color },
  ],
  // 名字下面的小提示：只保留昨日剩余这个有实际意义的
  nameNote: (m) => (m.yesterdayPending > 0 ? (
    <div style={{ fontSize: 10, color: 'var(--c-danger)', marginTop: 1, lineHeight: 1 }}>
      昨剩{m.yesterdayPending}张
    </div>
  ) : null),
  // 列头（数字区）
  head: () => (
    <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
      <div style={thStyle(COL.d)}>提量</div>
      <div style={thStyle(COL.s)}>成功</div>
      <div style={thStyle(COL.u)}>图片</div>
      <div style={thStyle(COL.dup)}>重复</div>
      <div style={thStyle(COL.inv)}>无效</div>
      <div style={thStyle(COL.fail)}>失败</div>
      <div style={{ ...thStyle(COL.r), textAlign: 'center', paddingRight: 0 }}>转换率</div>
    </div>
  ),
  // 成员行的数字区：固定宽度不压缩
  rowCells: (m) => {
    const ocrTotal = (m.ocrQualified || 0) * 2 + (m.ocrDuplicate || 0) + (m.ocrInvalid || 0) + (m.ocrFailed || 0);
    const ocrRunning = m.uploadCount > 0 && ocrTotal < m.uploadCount;
    const numCell = (val, color, w = COL.d) => (
      <div style={{
        width: w, textAlign: 'right', flexShrink: 0,
        fontSize: 13, fontWeight: val > 0 ? 600 : 400,
        fontVariantNumeric: 'tabular-nums',
        color: val > 0 ? color : 'var(--c-text-tertiary)',
      }}>
        {val > 0 ? val : '—'}
      </div>
    );
    return (
      <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        {numCell(m.dispatchCount || 0, 'var(--c-text)', COL.d)}

        {/* 成功 */}
        <div style={{ width: COL.s, textAlign: 'right', flexShrink: 0 }}>
          {ocrRunning && !(m.ocrQualified > 0)
            ? <span style={{ fontSize: 11, color: 'var(--c-text-tertiary)' }}>—</span>
            : <span style={{ fontSize: 13, fontWeight: 700,
                color: (m.ocrQualified || 0) > 0 ? 'var(--c-success)' : 'var(--c-text-tertiary)' }}>
                {(m.ocrQualified || 0) > 0 ? m.ocrQualified : '—'}
              </span>}
        </div>

        {/* 图片 */}
        <div style={{ width: COL.u, textAlign: 'right', flexShrink: 0 }}>
          <span style={{ fontSize: 13, fontWeight: 600,
            color: m.uploadCount > 0 ? 'var(--c-primary)' : 'var(--c-text-tertiary)' }}>
            {m.uploadCount > 0 ? m.uploadCount : '—'}
          </span>
          {ocrRunning && (
            <div style={{ fontSize: 9, color: 'var(--c-accent)', marginTop: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
              <Loader size={7} className="spin" />识
            </div>
          )}
        </div>

        {/* 重复 */}
        <div style={{ width: COL.dup, textAlign: 'right', flexShrink: 0, fontSize: 12,
          color: (m.ocrDuplicate||0) > 0 ? 'var(--c-warning)' : 'var(--c-text-tertiary)',
          fontVariantNumeric: 'tabular-nums' }}>
          {ocrRunning && !(m.ocrQualified > 0) ? '—' : ((m.ocrDuplicate||0) > 0 ? m.ocrDuplicate : '—')}
        </div>

        {/* 无效 */}
        <div style={{ width: COL.inv, textAlign: 'right', flexShrink: 0, fontSize: 12,
          color: (m.ocrInvalid||0) > 0 ? 'var(--c-danger)' : 'var(--c-text-tertiary)',
          fontVariantNumeric: 'tabular-nums' }}>
          {ocrRunning && !(m.ocrQualified > 0) ? '—' : ((m.ocrInvalid||0) > 0 ? m.ocrInvalid : '—')}
        </div>

        {/* 失败 */}
        <div style={{ width: COL.fail, textAlign: 'right', flexShrink: 0, fontSize: 12,
          color: (m.ocrFailed||0) > 0 ? 'var(--c-danger)' : 'var(--c-text-tertiary)',
          fontVariantNumeric: 'tabular-nums' }}>
          {ocrRunning && !(m.ocrQualified > 0) ? '—' : ((m.ocrFailed||0) > 0 ? m.ocrFailed : '—')}
        </div>

        {/* 转换率 */}
        <div style={{ width: COL.r, textAlign: 'right', flexShrink: 0, paddingLeft: 4 }}>
          <RatePill dispatch={m.dispatchCount} qualified={m.ocrQualified} />
        </div>
      </div>
    );
  },
  // 分组头（代理 / 子代理）的数字区：small = 子代理那一行（字号小一号）
  groupCells: (t, small) => {
    const fs = small ? 11 : 12;
    return (
      <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        <div style={{ width: COL.d, textAlign: 'right', fontSize: fs, fontWeight: 600,
          fontVariantNumeric: 'tabular-nums' }}>{t.D > 0 ? t.D : '—'}</div>
        <div style={{ width: COL.s, textAlign: 'right', fontSize: fs, fontWeight: 600,
          color: 'var(--c-success)', fontVariantNumeric: 'tabular-nums' }}>{t.S > 0 ? t.S : '—'}</div>
        <div style={{ width: COL.u, textAlign: 'right', fontSize: fs, fontWeight: 600,
          color: 'var(--c-primary)', fontVariantNumeric: 'tabular-nums' }}>{t.U > 0 ? t.U : '—'}</div>
        <div style={{ width: COL.dup }} /><div style={{ width: COL.inv }} /><div style={{ width: COL.fail }} />
        <div style={{ width: COL.r, paddingLeft: 4 }}>
          <RatePill dispatch={t.base} qualified={t.ok} size={small ? 'sm' : 'md'} />
        </div>
      </div>
    );
  },
};

// ── 证券的列：领取 / 成功 / 待核 / 作废 / 转化率（转化率 = 成功 ÷ 领取，阈值和排五同一套） ──
const PCOL = { claim: 40, ok: 34, pend: 34, rej: 30, rate: 52 };

const puzzleNum = (w, v, color, fs = 13) => (
  <div style={{
    width: w, textAlign: 'right', flexShrink: 0,
    fontSize: fs, fontWeight: v > 0 ? 600 : 400,
    fontVariantNumeric: 'tabular-nums',
    color: v > 0 ? color : 'var(--c-text-tertiary)',
  }}>
    {v > 0 ? v : '—'}
  </div>
);

const PUZZLE_VIEW = {
  key: 'puzzle',
  emptyAll: '这一天没有证券数据',
  emptyAgent: '该代理商名下这一天没有证券数据',
  footnote: '领取 = 当天领取的数据包里的号码数；成功 = 当天提交并已计入的单数；转化率 = 成功 ÷ 领取。待核实、作废按提交当天统计。',
  // 代理商角色：证券接口只返回“这一天有数据的人”，代理自己没数据时成员里就没有他，需要自己补一个用来自动选中
  selfFallback: true,
  // 证券接口里没归属代理商的人（管理员直属）也有数据：单独列一组，不让他们从报表里消失
  showUngrouped: true,
  // 服务端本来就只返回这一天有数据的人
  active: (m) => m.claimedNumbers > 0 || m.claimedPackages > 0 || m.success > 0 || m.pending > 0 || m.rejected > 0 || m.holding > 0,
  counted: (m) => m.claimedNumbers > 0 || m.success > 0,
  // 成功多的在前；成功一样多的，领取多的在前
  score: (m) => (m.success || 0) * 100000 + (m.claimedNumbers || 0),
  sum: (list) => {
    const C = list.reduce((s, m) => s + (m.claimedNumbers || 0), 0);
    const S = list.reduce((s, m) => s + (m.success || 0), 0);
    const P = list.reduce((s, m) => s + (m.pending || 0), 0);
    const R = list.reduce((s, m) => s + (m.rejected || 0), 0);
    const K = list.reduce((s, m) => s + (m.claimedPackages || 0), 0);
    return { C, S, P, R, K, base: C, ok: S };
  },
  summary: (t) => [
    { val: t.S, label: '成功单', color: 'var(--c-success)' },
    { val: t.C, label: `领取号码（${t.K} 包）`, color: 'var(--c-primary)' },
    { val: rateInfo(t.base, t.ok).text, label: '转化率', color: rateInfo(t.base, t.ok).color },
  ],
  nameNote: (m) => (m.holding > 0 ? (
    <div style={{ fontSize: 10, color: 'var(--c-accent)', marginTop: 1, lineHeight: 1 }}>
      手里还有 {m.holding} 个包
    </div>
  ) : null),
  head: () => (
    <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
      <div style={thStyle(PCOL.claim)}>领取</div>
      <div style={thStyle(PCOL.ok)}>成功</div>
      <div style={thStyle(PCOL.pend)}>待核</div>
      <div style={thStyle(PCOL.rej)}>作废</div>
      <div style={{ ...thStyle(PCOL.rate), textAlign: 'center', paddingRight: 0 }}>转化率</div>
    </div>
  ),
  rowCells: (m) => (
    <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
      {puzzleNum(PCOL.claim, m.claimedNumbers || 0, 'var(--c-text)')}
      {puzzleNum(PCOL.ok, m.success || 0, 'var(--c-success)')}
      {puzzleNum(PCOL.pend, m.pending || 0, 'var(--c-accent)')}
      {puzzleNum(PCOL.rej, m.rejected || 0, 'var(--c-danger)')}
      <div style={{ width: PCOL.rate, textAlign: 'right', flexShrink: 0, paddingLeft: 4 }}>
        <RatePill dispatch={m.claimedNumbers} qualified={m.success} />
      </div>
    </div>
  ),
  groupCells: (t, small) => {
    const fs = small ? 11 : 12;
    return (
      <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        <div style={{ width: PCOL.claim, textAlign: 'right', fontSize: fs, fontWeight: 600,
          fontVariantNumeric: 'tabular-nums' }}>{t.C > 0 ? t.C : '—'}</div>
        <div style={{ width: PCOL.ok, textAlign: 'right', fontSize: fs, fontWeight: 600,
          color: 'var(--c-success)', fontVariantNumeric: 'tabular-nums' }}>{t.S > 0 ? t.S : '—'}</div>
        <div style={{ width: PCOL.pend, textAlign: 'right', fontSize: fs, fontWeight: 600,
          color: 'var(--c-accent)', fontVariantNumeric: 'tabular-nums' }}>{t.P > 0 ? t.P : '—'}</div>
        <div style={{ width: PCOL.rej, textAlign: 'right', fontSize: fs, fontWeight: 600,
          color: 'var(--c-danger)', fontVariantNumeric: 'tabular-nums' }}>{t.R > 0 ? t.R : '—'}</div>
        <div style={{ width: PCOL.rate, paddingLeft: 4 }}>
          <RatePill dispatch={t.base} qualified={t.ok} size={small ? 'sm' : 'md'} />
        </div>
      </div>
    );
  },
};

// ── 成员行 ──
function MemberRow({ view, m, indent = 0, isLast = false }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center',
      padding: `7px 10px 7px ${10 + indent}px`,
      borderBottom: isLast ? 'none' : '0.5px solid var(--c-border)',
      minHeight: 38,
    }}>
      {/* 名字列：去掉角色标签，只保留有实际意义的小提示 */}
      <div style={{ flex: 1, minWidth: 0, marginRight: 6 }}>
        <div style={{
          fontSize: 13, fontWeight: 500,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          color: 'var(--c-text)',
        }}>{m.name || m.phone}</div>
        {view.nameNote(m)}
      </div>

      {/* 数字列区 */}
      {view.rowCells(m)}
    </div>
  );
}

// ── 子代理分组头 ──
function SubAgentGroup({ view, subAgent, members: subMembers, agentColor, collapsedSet, onToggle }) {
  const gid = `sub-${subAgent.id}`;
  const isCol = collapsedSet.has(gid);
  // 汇总包含次代自己 + 其名下成员（subMembers 已由调用方传入，含次代本人）
  const t = view.sum(subMembers);
  // 展开时只列次代名下的普通成员，次代本人已融入分组头，不再单独显示
  const sorted = [...subMembers]
    .filter(m => m.id !== subAgent.id)
    .sort((a, b) => view.score(b) - view.score(a));
  return (
    <>
      <div onClick={() => onToggle(gid)} style={{
        display: 'flex', alignItems: 'center', gap: 5,
        padding: '0 10px 0 20px', height: 40,
        background: 'var(--c-bg)',
        borderBottom: '0.5px solid var(--c-border)',
        cursor: 'pointer', userSelect: 'none',
      }}>
        <div style={{ width: 3, height: 16, borderRadius: 2, background: agentColor, flexShrink: 0 }} />
        <span style={{ fontSize: 12, fontWeight: 600, flex: 1, minWidth: 0,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subAgent.name}</span>
        {view.groupCells(t, true)}
        {isCol
          ? <ChevronRight size={12} color="var(--c-text-tertiary)" style={{ flexShrink: 0 }} />
          : <ChevronDown size={12} color="var(--c-text-tertiary)" style={{ flexShrink: 0 }} />}
      </div>
      {!isCol && sorted.map((m, i) =>
        <MemberRow key={m.id} view={view} m={m} indent={12} isLast={i === sorted.length - 1} />)}
    </>
  );
}

// ── 顶层代理分组头 ──
function TopAgentGroup({ view, agent, allMembers, subAgents, childrenOf, collapsedSet, onToggle }) {
  const gid = `top-${agent.id}`;
  const isCol = collapsedSet.has(gid);
  const color = AGENT_COLORS[agent._colorIdx % AGENT_COLORS.length] || '#007AFF';

  const descIds = collectIds(childrenOf, agent.id, false);
  // 含代理本人的全部成员（代理自己的数据也算进分组头汇总）
  const groupMembers = allMembers.filter(m =>
    (descIds.has(m.id) || m.id === agent.id) && view.active(m)
  );
  if (groupMembers.length === 0) return null;

  const t = view.sum(groupMembers);

  // 直属成员：排除代理本人（已融入分组头）和次代名下成员
  const directMembers = groupMembers
    .filter(m => m.id !== agent.id && !subAgents.some(sa => collectIds(childrenOf, sa.id, false).has(m.id)))
    .sort((a, b) => view.score(b) - view.score(a));

  return (
    <>
      <div onClick={() => onToggle(gid)} style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '0 10px', height: 44,
        borderBottom: '0.5px solid var(--c-border)',
        cursor: 'pointer', userSelect: 'none',
      }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
        <span style={{ fontSize: 13, fontWeight: 600, flex: 1, minWidth: 0,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{agent.name}</span>
        {view.groupCells(t, false)}
        {isCol
          ? <ChevronRight size={13} color="var(--c-text-tertiary)" style={{ flexShrink: 0 }} />
          : <ChevronDown size={13} color="var(--c-text-tertiary)" style={{ flexShrink: 0 }} />}
      </div>
      {!isCol && (
        <>
          {directMembers.map((m, i) =>
            <MemberRow key={m.id} view={view} m={m} indent={0}
              isLast={subAgents.length === 0 && i === directMembers.length - 1} />)}
          {subAgents.map((sa) => {
            const saIds = collectIds(childrenOf, sa.id, false);
            // 次代名下普通成员（不含次代本人，次代本人已融入 SubAgentGroup 分组头）
            const saMembers = groupMembers.filter(m => saIds.has(m.id) && m.id !== sa.id);
            const saWithSelf = allMembers.find(m => m.id === sa.id);
            const allSaMembers = saWithSelf ? [saWithSelf, ...saMembers] : saMembers;
            return (
              <SubAgentGroup key={sa.id} view={view} subAgent={sa} members={allSaMembers}
                agentColor={color} collapsedSet={collapsedSet} onToggle={onToggle} />
            );
          })}
        </>
      )}
    </>
  );
}

// ── 没有归属代理商的人（管理员直属 / 无归属）：样式和顶层代理分组一样，圆点用灰色 ──
function UngroupedGroup({ view, members: list, collapsedSet, onToggle }) {
  const gid = 'top-ungrouped';
  const isCol = collapsedSet.has(gid);
  if (list.length === 0) return null;
  const t = view.sum(list);
  const sorted = [...list].sort((a, b) => view.score(b) - view.score(a));
  return (
    <>
      <div onClick={() => onToggle(gid)} style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '0 10px', height: 44,
        borderBottom: '0.5px solid var(--c-border)',
        cursor: 'pointer', userSelect: 'none',
      }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--c-text-tertiary)', flexShrink: 0 }} />
        <span style={{ fontSize: 13, fontWeight: 600, flex: 1, minWidth: 0,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>直属用户</span>
        {view.groupCells(t, false)}
        {isCol
          ? <ChevronRight size={13} color="var(--c-text-tertiary)" style={{ flexShrink: 0 }} />
          : <ChevronDown size={13} color="var(--c-text-tertiary)" style={{ flexShrink: 0 }} />}
      </div>
      {!isCol && sorted.map((m, i) =>
        <MemberRow key={m.id} view={view} m={m} indent={0} isLast={i === sorted.length - 1} />)}
    </>
  );
}

// ── 页面主体（排五 / 证券共用） ──
// data = { members, agents }；取数、刷新、日期由各业务页面负责，这里只负责版式和“按代理筛选 / 分组 / 折叠”
function AgentReport({ view, data, loading, lastRefresh, selectedDate, onDateChange, onRefresh, onPick }) {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';

  const [selectedAgent, setSelectedAgent] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [collapsedSet, setCollapsedSet] = useState(new Set());
  const [autoSelected, setAutoSelected] = useState(false);

  const members = useMemo(() => data?.members || [], [data]);
  const agents  = useMemo(() => data?.agents  || [], [data]);

  const childrenOf = useMemo(() => {
    const map = new Map();
    const add = (pid, cid) => {
      if (pid == null) return;
      if (!map.has(pid)) map.set(pid, []);
      if (!map.get(pid).includes(cid)) map.get(pid).push(cid);
    };
    agents.forEach(a => add(a.owner_id, a.id));
    members.forEach(m => add(m.owner_id, m.id));
    return map;
  }, [agents, members]);

  const topAgents = useMemo(() =>
    agents.filter(a => a.owner_role !== 'agent').map((a, i) => ({ ...a, _colorIdx: i })),
    [agents]);

  const subAgentMap = useMemo(() => {
    const map = new Map();
    agents.filter(a => a.owner_role === 'agent').forEach(sa => {
      const topOwner = (() => {
        let cur = sa.owner_id;
        for (let i = 0; i < 10; i++) {
          const owner = agents.find(a => a.id === cur);
          if (!owner) break;
          if (owner.owner_role !== 'agent') return owner.id;
          cur = owner.owner_id;
        }
        return cur;
      })();
      if (!map.has(topOwner)) map.set(topOwner, []);
      map.get(topOwner).push(sa);
    });
    return map;
  }, [agents]);

  const visibleAgents = useMemo(() => {
    if (isAdmin) return topAgents;
    let self = members.find(m => m.id === currentUser?.id && m.role === 'agent');
    if (!self && view.selfFallback && currentUser?.role === 'agent') self = { id: currentUser.id, name: currentUser.name || currentUser.username || '我', role: 'agent' };
    return self ? [{ ...self, _colorIdx: 0 }] : [];
  }, [isAdmin, topAgents, members, currentUser, view]);

  // ── 代理商角色：数据加载后自动选中自己 ──
  useEffect(() => {
    if (!isAdmin && !autoSelected && visibleAgents.length > 0 && members.length > 0) {
      setSelectedAgent(visibleAgents[0].id);
      setAutoSelected(true);
    }
  }, [isAdmin, autoSelected, visibleAgents, members]);

  const filteredMembers = useMemo(() => {
    if (!selectedAgent) return members.filter(view.active);
    const ids = collectIds(childrenOf, selectedAgent, true);
    return members.filter(m => ids.has(m.id));
  }, [members, childrenOf, selectedAgent, view]);

  const totals = view.sum(filteredMembers);

  const toggleCollapse = (gid) => {
    setCollapsedSet(prev => {
      const next = new Set(prev);
      next.has(gid) ? next.delete(gid) : next.add(gid);
      return next;
    });
  };

  // 某个代理（含名下各级）“算数”的人：选择器里的“N 人”和药丸按这批人算
  const countedUnder = (agentId) => {
    const ids = collectIds(childrenOf, agentId, true);
    return members.filter(m => ids.has(m.id) && view.counted(m));
  };

  return (
    <div className="page">
      {/* ── 导航 ── */}
      <div className="header">
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/admin')} style={{ padding: '6px 8px' }}>
          <ArrowLeft size={18} />
        </button>
        <span style={{ flex: 1 }}>交单报表</span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <AdminNotificationBell />
          <button className="btn btn-outline btn-sm" onClick={onRefresh} disabled={loading}
            style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <RefreshCw size={14} className={loading ? 'spin' : ''} />刷新
          </button>
        </div>
      </div>

      <div style={{ padding: '12px 16px', paddingBottom: 80 }}>
        <ReportTabs current={view.key} onPick={onPick} />
        {/* ── 筛选卡 ── */}
        <div className="card" style={{ marginBottom: 4 }}>
          {/* 日期 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: visibleAgents.length > 0 ? 12 : 0 }}>
            <span style={{ fontSize: 13, color: 'var(--c-text-secondary)', flexShrink: 0 }}>查看日期</span>
            <input className="input" type="date" value={selectedDate}
              onChange={e => onDateChange(e.target.value)} style={{ flex: 1 }} />
          </div>

          {/* ── 代理下拉选择器 ── */}
          {visibleAgents.length > 0 && (() => {
            const selAgent = visibleAgents.find(a => a.id === selectedAgent);
            const selColor = selAgent ? AGENT_COLORS[selAgent._colorIdx % AGENT_COLORS.length] : 'var(--c-text-tertiary)';
            const selLabel = selAgent ? selAgent.name : '全部代理商';
            const selRight = selAgent ? `${countedUnder(selAgent.id).length} 人` : '';
            return (
              <div style={{ position: 'relative' }}>
                <div onClick={() => setPickerOpen(o => !o)} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '9px 10px', borderRadius: 10,
                  border: '1px solid var(--c-border)',
                  background: 'var(--c-bg)', cursor: 'pointer',
                }}>
                  <div style={{ width: 9, height: 9, borderRadius: '50%', background: selColor, flexShrink: 0 }} />
                  <span style={{ fontSize: 14, fontWeight: 500, flex: 1, color: 'var(--c-text)' }}>{selLabel}</span>
                  <span style={{ fontSize: 12, color: 'var(--c-text-secondary)' }}>{selRight}</span>
                  {pickerOpen
                    ? <ChevronDown size={14} color="var(--c-text-tertiary)" style={{ flexShrink: 0 }} />
                    : <ChevronRight size={14} color="var(--c-text-tertiary)" style={{ flexShrink: 0 }} />}
                </div>
                {pickerOpen && (
                  <div style={{
                    position: 'absolute', left: 0, right: 0, top: 'calc(100% + 4px)',
                    background: 'var(--c-surface, #fff)', borderRadius: 12,
                    border: '1px solid var(--c-border)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                    overflow: 'hidden', zIndex: 50,
                  }}>
                    {[{ id: null, name: '全部代理商', _colorIdx: -1 }, ...visibleAgents].map((a, idx) => {
                      const isSelected = a.id === selectedAgent;
                      const color = a._colorIdx >= 0 ? AGENT_COLORS[a._colorIdx % AGENT_COLORS.length] : 'var(--c-text-tertiary)';
                      const aMs = a.id ? countedUnder(a.id) : members.filter(view.counted);
                      const aSum = view.sum(aMs);
                      return (
                        <div key={a.id ?? 'all'} onClick={() => { setSelectedAgent(a.id); setPickerOpen(false); }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '11px 14px',
                            borderBottom: idx < visibleAgents.length ? '0.5px solid var(--c-border)' : 'none',
                            background: isSelected ? 'rgba(0,122,255,0.04)' : 'transparent',
                            cursor: 'pointer',
                          }}>
                          <div style={{ width: 9, height: 9, borderRadius: '50%', background: color, flexShrink: 0 }} />
                          <span style={{ fontSize: 14, fontWeight: 500, flex: 1 }}>{a.name}</span>
                          <RatePill dispatch={aSum.base} qualified={aSum.ok} size="sm" />
                          {isSelected && <Check size={14} color="var(--c-primary)" style={{ flexShrink: 0 }} />}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* 已选提示 */}
        {selectedAgent && (() => {
          const ag = visibleAgents.find(a => a.id === selectedAgent) || members.find(m => m.id === selectedAgent);
          return ag ? (
            <div style={{ fontSize: 12, color: 'var(--c-primary)', marginBottom: 4,
              display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: 'var(--c-text-secondary)' }}>已选：</span>
              <span style={{ fontWeight: 600 }}>{ag.name || ag.phone}</span>
              <span style={{ color: 'var(--c-text-secondary)' }}>名下 · 共 {filteredMembers.length} 人</span>
              {isAdmin && (
                <button onClick={() => setSelectedAgent(null)}
                  style={{ fontSize: 11, color: 'var(--c-text-tertiary)', background: 'none',
                    border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
                  清除
                </button>
              )}
            </div>
          ) : null;
        })()}

        {/* 更新时间 */}
        {lastRefresh && (
          <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', textAlign: 'right', marginBottom: 8 }}>
            最后更新：{lastRefresh.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </div>
        )}

        {/* ── 汇总卡 ── */}
        {filteredMembers.length > 0 && (
          <div className="card" style={{ display: 'flex', gap: 0, marginBottom: 12, padding: 0, overflow: 'hidden' }}>
            {view.summary(totals).map((s, i, arr) => (
              <React.Fragment key={i}>
                <div style={{ flex: 1, padding: '14px 8px', textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: s.color,
                    fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.3px' }}>{s.val}</div>
                  <div style={{ fontSize: 11, color: 'var(--c-text-secondary)', marginTop: 3 }}>{s.label}</div>
                </div>
                {i < arr.length - 1 && <div style={{ width: 0.5, background: 'var(--c-divider)', margin: '10px 0' }} />}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* ── 成员明细表 ── */}
        {loading ? (
          <p style={{ textAlign: 'center', padding: 40, color: 'var(--c-text-secondary)' }}>加载中...</p>
        ) : members.length === 0 ? (
          <div className="card empty-state">
            <TrendingUp size={40} color="var(--c-text-secondary)" strokeWidth={1.2}
              style={{ margin: '0 auto 12px', display: 'block' }} />
            <p>{view.emptyAll}</p>
          </div>
        ) : filteredMembers.length === 0 ? (
          <div className="card empty-state">
            <TrendingUp size={40} color="var(--c-text-secondary)" strokeWidth={1.2}
              style={{ margin: '0 auto 12px', display: 'block' }} />
            <p style={{ color: 'var(--c-text-secondary)' }}>{view.emptyAgent}</p>
          </div>
        ) : (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {/* 列头 */}
            <div style={{
              display: 'flex', alignItems: 'center',
              padding: '6px 10px',
              borderBottom: '0.5px solid var(--c-border)',
              background: 'var(--c-bg)',
            }}>
              <div style={{ flex: 1, fontSize: 10, fontWeight: 600,
                color: 'var(--c-text-secondary)', marginRight: 6 }}>成员</div>
              {view.head()}
            </div>

            {/* 内容区 */}
            {selectedAgent ? (
              (() => {
                const ag = visibleAgents.find(a => a.id === selectedAgent);
                const color = ag ? AGENT_COLORS[ag._colorIdx % AGENT_COLORS.length] : '#007AFF';
                const subAgents = subAgentMap.get(selectedAgent) || [];
                // 直属普通成员：排除代理本人（已融入分组头）和次代名下成员
                const directMs = filteredMembers
                  .filter(m => m.id !== selectedAgent && !subAgents.some(sa => collectIds(childrenOf, sa.id, false).has(m.id)))
                  .sort((a, b) => view.score(b) - view.score(a));
                return (
                  <>
                    {directMs.map((m, i) => <MemberRow key={m.id} view={view} m={m} isLast={subAgents.length === 0 && i === directMs.length - 1} />)}
                    {subAgents.map(sa => {
                      const saIds = collectIds(childrenOf, sa.id, false);
                      const saMs = filteredMembers.filter(m => saIds.has(m.id) && m.id !== sa.id);
                      const saWithSelf = filteredMembers.find(m => m.id === sa.id);
                      return <SubAgentGroup key={sa.id} view={view} subAgent={sa}
                        members={saWithSelf ? [saWithSelf, ...saMs] : saMs}
                        agentColor={color} collapsedSet={collapsedSet} onToggle={toggleCollapse} />;
                    })}
                  </>
                );
              })()
            ) : (
              <>
                {topAgents
                  .filter(ag => {
                    const ids = collectIds(childrenOf, ag.id, true);
                    return members.some(m => ids.has(m.id) && view.active(m));
                  })
                  .map(ag => (
                    <TopAgentGroup key={ag.id} view={view} agent={ag} allMembers={members}
                      subAgents={subAgentMap.get(ag.id) || []}
                      childrenOf={childrenOf}
                      collapsedSet={collapsedSet} onToggle={toggleCollapse} />
                  ))}
                {view.showUngrouped && (() => {
                  const grouped = new Set();
                  topAgents.forEach(ag => collectIds(childrenOf, ag.id, true).forEach(id => grouped.add(id)));
                  return <UngroupedGroup view={view} members={members.filter(m => view.active(m) && !grouped.has(m.id))}
                    collapsedSet={collapsedSet} onToggle={toggleCollapse} />;
                })()}
              </>
            )}
          </div>
        )}

        {view.footnote && (
          <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', lineHeight: 1.7, marginTop: 10 }}>{view.footnote}</div>
        )}
      </div>

      <TabBar mode="admin" />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// 交单报表 · 排五：取数 + 刷新，版式交给 AgentReport
// ══════════════════════════════════════════════════════════════════════════════
function LegacySubmissionRatePage({ onPick }) {
  const { subscribe } = useSocket();
  const { showToast } = useToast();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const params = selectedDate ? { date: selectedDate } : {};
      const res = await api.get('/admin/submission-rate', { params });
      setData(res.data);
      setLastRefresh(new Date());
      if (!selectedDate && res.data.date) setSelectedDate(res.data.date);
    } catch (err) {
      if (err.response?.status !== 401) showToast('加载失败', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast, selectedDate]);

  useEffect(() => { fetchData(); }, [fetchData, selectedDate]);

  useEffect(() => {
    if (!subscribe) return;
    return subscribe((event) => {
      if (['stats_refresh', 'image_stats_refresh', 'data_refresh', 'ocr_batch_done'].includes(event))
        fetchData();
    });
  }, [subscribe, fetchData]);

  return <AgentReport view={LEGACY_VIEW} data={data} loading={loading} lastRefresh={lastRefresh}
    selectedDate={selectedDate} onDateChange={setSelectedDate} onRefresh={fetchData} onPick={onPick} />;
}

// ══════════════════════════════════════════════════════════════════════════════
// 交单报表 · 证券：按天看每个人的领取 / 成功 / 待核实 / 作废 / 转化率。取数 + 刷新，版式和排五共用
// ══════════════════════════════════════════════════════════════════════════════
function PuzzleSubmissionRatePage({ onPick }) {
  const { subscribe } = useSocket();
  const { showToast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/puzzle/report', { params: selectedDate ? { date: selectedDate } : {} });
      setData(res.data);
      setLastRefresh(new Date());
      if (!selectedDate && res.data.date) setSelectedDate(res.data.date);
    } catch (err) {
      if (err.response?.status !== 401) showToast('加载失败', 'error');
    } finally { setLoading(false); }
  }, [showToast, selectedDate]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    if (!subscribe) return undefined;
    return subscribe((event) => { if (['puzzle_updated', 'puzzle_session_updated', 'puzzle_settlement_updated', 'data_refresh'].includes(event)) fetchData(); });
  }, [subscribe, fetchData]);

  return <AgentReport view={PUZZLE_VIEW} data={data} loading={loading} lastRefresh={lastRefresh}
    selectedDate={selectedDate} onDateChange={setSelectedDate} onRefresh={fetchData} onPick={onPick} />;
}

// 报表顶部的三项标签：排五 / 证券 各看各的口径；汇总只放两边都能相加的“成功单”
function ReportTabs({ current, onPick }) {
  const items = [
    { key: 'legacy', label: '排五', Icon: ClipboardList },
    { key: 'puzzle', label: '证券', Icon: Puzzle },
    { key: 'all',    label: '汇总', Icon: Layers },
  ];
  return (
    <div style={{ display: 'flex', gap: 6, padding: 4, background: 'var(--c-surface)', borderRadius: 12, boxShadow: 'var(--shadow-sm)', marginBottom: 12 }}>
      {items.map(({ key, label, Icon }) => {
        const active = current === key;
        return (
          <button key={key} onClick={() => onPick(key)}
            style={{
              flex: 1, height: 36, borderRadius: 9, border: 'none', cursor: 'pointer',
              background: active ? 'var(--c-primary)' : 'transparent',
              color: active ? '#fff' : 'var(--c-text-secondary)',
              fontSize: 14, fontWeight: active ? 700 : 500,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, transition: 'all .15s',
            }}>
            <Icon size={15} /> {label}
          </button>
        );
      })}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// 交单报表 · 汇总：每人一行，排五成功组数 + 证券成功单数 = 合计成功
// 只放两个业务都能相加的“成功”。转化率的分母不同（排五 ÷ 提取条数，证券 ÷ 领取号码），不合并，仍在各自页面里看。
// 数据直接取两个业务已有的报表接口（/admin/submission-rate 和 /puzzle/report），按用户 id 合并。
// ══════════════════════════════════════════════════════════════════════════════
export function CombinedReportPage({ onPick }) {
  const navigate = useNavigate();
  const { subscribe } = useSocket();
  const { user: currentUser } = useAuth();
  const { showToast } = useToast();
  const isAdmin = currentUser?.role === 'admin';
  const [legacy, setLegacy] = useState(null);
  const [puzzle, setPuzzle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedAgent, setSelectedAgent] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const params = selectedDate ? { date: selectedDate } : {};
      const [a, b] = await Promise.all([
        api.get('/admin/submission-rate', { params }),
        api.get('/puzzle/report', { params }),
      ]);
      setLegacy(a.data); setPuzzle(b.data);
      if (!selectedDate && (a.data.date || b.data.date)) setSelectedDate(a.data.date || b.data.date);
    } catch (err) {
      if (err.response?.status !== 401) showToast('加载失败', 'error');
    } finally { setLoading(false); }
  }, [showToast, selectedDate]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    if (!subscribe) return undefined;
    return subscribe((event) => {
      if (['stats_refresh', 'image_stats_refresh', 'data_refresh', 'ocr_batch_done', 'puzzle_updated', 'puzzle_session_updated', 'puzzle_settlement_updated'].includes(event)) fetchData();
    });
  }, [subscribe, fetchData]);

  // 两边的成员按 id 合并
  const merged = useMemo(() => {
    const map = new Map();
    const get = (m) => { if (!map.has(m.id)) map.set(m.id, { id: m.id, name: m.name, role: m.role, is_external: m.is_external, owner_id: m.owner_id ?? null, legacy: 0, puzzle: 0 }); return map.get(m.id); };
    (legacy?.members || []).forEach(m => { get(m).legacy = m.ocrQualified || 0; });
    (puzzle?.members || []).forEach(m => { get(m).puzzle = m.success || 0; });
    return [...map.values()];
  }, [legacy, puzzle]);
  const agents = useMemo(() => {
    const seen = new Map();
    [...(legacy?.agents || []), ...(puzzle?.agents || [])].forEach(a => { if (!seen.has(a.id)) seen.set(a.id, a); });
    return [...seen.values()];
  }, [legacy, puzzle]);

  const shown = useMemo(() => {
    if (!selectedAgent) return merged;
    const childrenOf = new Map();
    [...agents, ...merged].forEach(x => { if (x.owner_id != null) { if (!childrenOf.has(x.owner_id)) childrenOf.set(x.owner_id, []); childrenOf.get(x.owner_id).push(x.id); } });
    const ids = new Set([Number(selectedAgent)]); const q = [Number(selectedAgent)];
    while (q.length) { const c = q.shift(); for (const k of (childrenOf.get(c) || [])) if (!ids.has(k)) { ids.add(k); q.push(k); } }
    return merged.filter(m => ids.has(m.id));
  }, [merged, agents, selectedAgent]);

  const rows = useMemo(() => shown.filter(m => m.legacy > 0 || m.puzzle > 0).sort((a, b) => (b.legacy + b.puzzle) - (a.legacy + a.puzzle) || a.id - b.id), [shown]);
  const total = useMemo(() => rows.reduce((t, m) => ({ legacy: t.legacy + m.legacy, puzzle: t.puzzle + m.puzzle }), { legacy: 0, puzzle: 0 }), [rows]);
  const cell = (v, color) => <div style={{ width: 52, textAlign: 'right', flexShrink: 0, fontSize: 13, fontWeight: v > 0 ? 600 : 400, fontVariantNumeric: 'tabular-nums', color: v > 0 ? color : 'var(--c-text-tertiary)' }}>{v > 0 ? v : '—'}</div>;
  const head = (t, w = 52) => <div style={{ width: w, textAlign: 'right', fontSize: 10, fontWeight: 600, color: 'var(--c-text-secondary)', flexShrink: 0 }}>{t}</div>;

  return (
    <div className="page">
      <div className="header">
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/admin')} style={{ padding: '6px 8px' }}>
          <ArrowLeft size={18} />
        </button>
        <span style={{ flex: 1 }}>交单报表 · 汇总</span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <AdminNotificationBell />
          <button className="btn btn-outline btn-sm" onClick={fetchData} disabled={loading} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <RefreshCw size={14} className={loading ? 'spin' : ''} />
          </button>
        </div>
      </div>
      <div style={{ padding: '12px 16px', paddingBottom: 80 }}>
        <ReportTabs current="all" onPick={onPick} />

        <div className="card" style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: isAdmin && agents.length ? 12 : 0 }}>
            <span style={{ fontSize: 13, color: 'var(--c-text-secondary)', flexShrink: 0 }}>日期</span>
            <input className="input" type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} style={{ flex: 1 }} />
          </div>
          {isAdmin && agents.length > 0 && (
            <select className="input" value={selectedAgent} onChange={e => setSelectedAgent(e.target.value)}>
              <option value="">全部代理商</option>
              {agents.map(a => <option key={a.id} value={a.id}>{a.owner_role === 'agent' ? '　└ ' : ''}{a.name}</option>)}
            </select>
          )}
        </div>

        <div className="card" style={{ display: 'flex', padding: '12px 0' }}>
          <div className="stat-card" style={{ flex: 1 }}><div className="stat-value" style={{ color: 'var(--c-success)' }}>{total.legacy + total.puzzle}</div><div className="stat-label">成功合计</div></div>
          <div style={{ width: 0.5, background: 'var(--c-divider)', margin: '10px 0' }} />
          <div className="stat-card" style={{ flex: 1 }}><div className="stat-value" style={{ color: 'var(--c-primary)' }}>{total.legacy}</div><div className="stat-label">排五（组）</div></div>
          <div style={{ width: 0.5, background: 'var(--c-divider)', margin: '10px 0' }} />
          <div className="stat-card" style={{ flex: 1 }}><div className="stat-value" style={{ color: '#8944ab' }}>{total.puzzle}</div><div className="stat-label">证券（单）</div></div>
        </div>

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', padding: '8px 10px', borderBottom: '0.5px solid var(--c-border)' }}>
            <div style={{ flex: 1, fontSize: 10, fontWeight: 600, color: 'var(--c-text-secondary)' }}>成员</div>
            {head('排五成功')}{head('证券成功')}{head('合计')}
          </div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 30, color: 'var(--c-text-tertiary)' }}><Loader size={20} className="spin" /></div>
          ) : rows.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 30, fontSize: 13, color: 'var(--c-text-tertiary)' }}>这一天没有成功记录</div>
          ) : rows.map((m, i) => (
            <div key={m.id} style={{ display: 'flex', alignItems: 'center', padding: '7px 10px', borderBottom: i < rows.length - 1 ? '0.5px solid var(--c-border)' : 'none', minHeight: 38 }}>
              <div style={{ flex: 1, minWidth: 0, marginRight: 6, fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {m.name}
                {m.role === 'agent' && <span className="badge badge-info" style={{ marginLeft: 5, fontSize: 9 }}>代理</span>}
                {m.is_external ? <span className="badge badge-warning" style={{ marginLeft: 5, fontSize: 9 }}>外部</span> : null}
              </div>
              {cell(m.legacy, 'var(--c-primary)')}
              {cell(m.puzzle, '#8944ab')}
              <div style={{ width: 52, textAlign: 'right', flexShrink: 0, fontSize: 14, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{m.legacy + m.puzzle}</div>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', lineHeight: 1.7, marginTop: 10 }}>
          排五成功 = 当天 OCR 成功的组数；证券成功 = 当天提交并已计入的单数。转化率的分母不同（排五 ÷ 提取条数，证券 ÷ 领取号码），不能相加，请到「排五」「证券」页里分别看。
        </div>
      </div>
      <TabBar mode="admin" />
    </div>
  );
}

// 交单报表：排五 / 证券 / 汇总 三个视图。排五、证券跟着全局业务选择（和其他页面一致），汇总是页内视图，不改全局选择。
export default function SubmissionRatePage() {
  const [bizMode] = useBizMode();
  const [showAll, setShowAll] = useState(false);
  const onPick = (key) => {
    if (key === 'all') { setShowAll(true); return; }
    setBizMode(key);
    setShowAll(false);
  };
  if (showAll) return <CombinedReportPage onPick={onPick} />;
  return bizMode === 'puzzle' ? <PuzzleSubmissionRatePage onPick={onPick} /> : <LegacySubmissionRatePage onPick={onPick} />;
}

